import { useNavigate } from 'react-router-dom';
import { ArrowClockwise, House } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { StatusScreen } from '@/components/StatusScreen';

export type AccessDeniedProps = {
  // Override the default copy when a page wants something specific.
  title?: string | undefined;
  body?: string | undefined;
  // Where "back to start" lands. Defaults to the org home.
  homeTo?: string | undefined;
  className?: string | undefined;
};

// AccessDenied is the on-brand stand-in for the old bare
// "You don't have permission to view this page." line. It covers two
// everyday cases that look identical to the user: a genuine permission
// gate, and a transient API hiccup that left a role check unresolved.
// So it offers both escape hatches — reload (fixes the hiccup) and back
// to start (fixes the wrong turn) — without claiming to know which it is.
export function AccessDenied({
  title,
  body,
  homeTo = '/',
  className,
}: AccessDeniedProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  return (
    <StatusScreen
      title={title ?? t('errors.accessDenied.title')}
      body={body ?? t('errors.accessDenied.body')}
      className={className}
      actions={[
        {
          label: t('errors.accessDenied.reload'),
          onClick: () => window.location.reload(),
          icon: <ArrowClockwise />,
        },
        {
          label: t('errors.accessDenied.home'),
          onClick: () => navigate(homeTo),
          icon: <House />,
          variant: 'outline',
        },
      ]}
    />
  );
}
